import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import axiosInstance from 'utils/axios';
import { parseFilters } from 'utils/parseFilters';

import { QUERY_KEYS } from 'api/certificates/queries';
import { DocumentModel, PUBLICATION_MODE } from 'api/documents/documents.model';
import { apiEndpoints } from 'apiEndpoints';
import { CertificateDetailsModel } from 'models/certificate.model';

import { FilterableQueryOptionsType, FilterableResponseType, FiltersInterface } from '../types';

const extenedEncodeURI = (value: string) => encodeURI(value.replace(/\\/g, '\\\\'));

const requestFiltersFormatter = (filters: FiltersInterface) => {
  const {
    documentType,
    draft,
    senderPublisherId,
    favourite,
    batteryPackSerialNumber,
    categoryId,
    categoryName,
    productId,
    creationDateRange,
  } = filters;

  const documentTypeFilter = documentType ? { documentType } : undefined;
  const draftFilter = draft !== undefined ? { draft } : undefined;
  const senderPublisherIdFilter = senderPublisherId ? { senderPublisherId } : undefined;
  const favouriteFilter = favourite !== undefined ? { favourite } : undefined;
  const batteryPackSerialNumberFilter = batteryPackSerialNumber
    ? { batteryPackSerialNumber: extenedEncodeURI(batteryPackSerialNumber) }
    : undefined;
  const categoryIdFilter = categoryId ? { categoryId } : undefined;
  const categoryNameFilter = categoryName ? { categoryName } : undefined;
  const productIdFilter = productId ? { productId: extenedEncodeURI(productId) } : undefined;
  const creationDateRangeFromFilter = creationDateRange?.from
    ? { 'creationDateRange.from': encodeURIComponent(creationDateRange?.from) }
    : undefined;

  return parseFilters({
    ...filters,
    filters: {
      ...documentTypeFilter,
      ...draftFilter,
      ...senderPublisherIdFilter,
      ...favouriteFilter,
      ...batteryPackSerialNumberFilter,
      ...categoryIdFilter,
      ...categoryNameFilter,
      ...productIdFilter,
      ...creationDateRangeFromFilter,
      publicationMode: PUBLICATION_MODE.NEW,
    },
  });
};

export const useCertificatesQuery = (
  filters: FiltersInterface,
  options?: FilterableQueryOptionsType<DocumentModel<CertificateDetailsModel>>
) =>
  useQuery<
    undefined,
    AxiosError<DocumentModel<CertificateDetailsModel>>,
    FilterableResponseType<DocumentModel<CertificateDetailsModel>>
  >(
    [QUERY_KEYS.GET_CERTIFICATES, filters],
    async () => {
      const { data } = await axiosInstance.get(apiEndpoints.documents.index, {
        params: requestFiltersFormatter(filters),
      });
      return data;
    },
    { ...options, refetchOnWindowFocus: false }
  );
